// @flow
import {compose} from 'redux';
import {connect} from 'react-redux';
import Typeahead from '@homeaway/react-destination-typeahead';
import {Button} from '@homeaway/react-buttons';
import {SVGIcon} from '@homeaway/react-svg';
import {FormattedMessage} from 'react-intl';
import {GUEST_MODAL, DATE_MODAL} from '../../actions/modals';
import {injectIntl, type IntlShape} from 'react-intl';
import {isDifferent} from '../../utils';
import {media} from '../../utils/breakpoints';
import {pick} from 'lodash';
import {type SearchParamsType} from '../../reducers/searchParams';
import classNames from 'classnames';
import ConnectedDateRangePicker from '../ConnectedDateRangePicker/ConnectedDateRangePicker';
import ConnectedGuestPicker from '../ConnectedGuestPicker/ConnectedGuestPicker';
import dateRangePickerActions from '../../actions/dateRangePicker';
import React, {Component} from 'react';
import routerActions from '../../actions/router';
import isBoundingBoxSearch from '../../utils/isBoundingBoxSearch';

import type {Element} from 'react';
import type {State as ReduxState} from '../../reducers';

import '@homeaway/react-destination-typeahead/lib/stylesheets/react-destination-typeahead.css';
import './SearchFields.less';

type DispatchProps = {
    onOpenDateRangePicker: () => void,
    onCloseDateRangePicker: () => void,
    onOpenGuestPicker: () => void,
    onCloseGuestPicker: () => void,
    setDateRangePickerDates: typeof dateRangePickerActions.setDateRangePickerDates
};

type StateProps = {
    dateRangePickerIsOpen: boolean,
    guestPickerIsOpen: boolean,
    transDates: any,
    isMobile: boolean,
    isTouchDevice: boolean,
    q: string,
    arrival: string,
    departure: string,
    adultsCount: number,
    childrenCount: number,
    petIncluded: boolean,
    pinnedPropertyListing: string,
    isBoundingBoxSearch: boolean,
    isRegionSearch: boolean
};

type PickerLabelProps = {
    textClass?: string,
    messageId: string | null,
    defaultText: string
};

export const PickerInputLabel = ({
    textClass = '',
    messageId,
    defaultText
}: PickerLabelProps) => {
    const message = messageId ? (
        <FormattedMessage id={messageId} defaultMessage={defaultText} />
    ) : (
        defaultText
    );

    return <span className={textClass}>{message}</span>;
};

type MessageDescriptor = {messageId: string, defaultText: string};

type OwnProps = {
    searchIcon?: Element<typeof SVGIcon> | boolean,
    showGuests?: boolean,
    searchButton?: Element<typeof Button>,
    datepickerConfig?: {
        positionIconRight?: boolean,
        calendarIcon?: Element<typeof Component>,
        noDatesDescriptor?: MessageDescriptor,
        hasDatesDescriptor?: MessageDescriptor
    },
    guestpickerConfig?: {
        guestsIcon?: Element<typeof SVGIcon>,
        noGuestDescriptor?: MessageDescriptor,
        hasGuestDescriptor?: MessageDescriptor
    },
    typeaheadConfig?: {messages: any}
};

type Props = StateProps &
    DispatchProps & {
        intl: IntlShape,
        onChange: (SearchParamsType) => void,
        onLocationChange?: (SearchParamsType) => void
    } & OwnProps;

export type State = {
    +q?: string,
    +arrival?: string,
    +departure?: string,
    +adultsCount?: number,
    +childrenCount?: number,
    +petIncluded?: boolean,
    +pinnedPropertyListing?: string | null
};

export type ComponentState = {
    q: string,
    arrival: string,
    departure: string,
    adultsCount: number,
    childrenCount: number,
    initialDates: {
        arrival: string,
        departure: string
    },
    petIncluded: boolean,
    pinnedPropertyListing: string | null
};

export const searchFieldParameters = [
    'q',
    'arrival',
    'departure',
    'adultsCount',
    'childrenCount',
    'petIncluded',
    'pinnedPropertyListing'
];

export class SearchFields extends Component<Props, ComponentState> {
    static defaultProps = {
        searchIcon: false,
        showGuests: true
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            ...pick(props, searchFieldParameters)
        };

        props.setDateRangePickerDates(
            this.useDefaultDateWhenNonExist(props.arrival),
            this.useDefaultDateWhenNonExist(props.departure)
        );
    }

    componentWillReceiveProps(nextProps: Props) {
        /**
         * We need to reset the initialValues when they change for the back button
         * to work
         */
        if (
            isDifferent(
                pick(nextProps, searchFieldParameters),
                pick(this.props, searchFieldParameters)
            )
        ) {
            this.setState({...pick(nextProps, searchFieldParameters)});
        }

        const nextDates = nextProps.transDates;
        if (
            !nextProps.dateRangePickerIsOpen &&
            this.props.dateRangePickerIsOpen
        ) {
            this.setState({
                arrival: this.useDefaultDateWhenNonExist(nextDates.arrival),
                departure: this.useDefaultDateWhenNonExist(nextDates.departure)
            });
        }
    }

    useDefaultDateWhenNonExist = (date: ?string): string => {
        if (date !== null && typeof date !== 'undefined') {
            return date;
        }
        return '';
    };

    handleDateRangeChange = (arrival: string, departure: string) => {
        this.setState({arrival, departure});
    };

    getSearchParams = (newState: State) => {
        let searchParams: SearchParamsType = {
            ...pick(this.state, searchFieldParameters),
            ...newState
        };
        Object.entries(searchParams).forEach(([param, value]) => {
            if (value === '') {
                searchParams[param] = null;
            }
        });
        return searchParams;
    };

    handleOnChange = (newState: State = {}) => {
        this.props.onChange(this.getSearchParams(newState));
    };

    handleOnLocationChange = (q: string) => {
        const hasRegionAndLocation =
            this.props.isBoundingBoxSearch || this.props.isRegionSearch;

        if (q === this.state.q && !hasRegionAndLocation) {
            return;
        }

        const newParams = {q, pinnedPropertyListing: null};
        this.setState(newParams);

        const onLocationChange =
            this.props.onLocationChange || this.props.onChange;
        onLocationChange(this.getSearchParams(newParams));
    };

    handleSelectDestination = () => {
        if (!this.hasDates()) {
            this.handleDateRangeOpen();
        }
    };

    hasDates = () => {
        const {arrival, departure} = this.state;
        return !!(arrival && departure);
    };

    handleGuestSelectorChange = (key: string, value: any) => {
        this.setState({[key]: value});
    };

    handleGuestSelectorClose = () => {
        const {onCloseGuestPicker} = this.props;

        this.handleOnChange();
        onCloseGuestPicker();

        this.handleGuestPickerUpdate();
    };

    handleGuestPickerUpdate = () => {
        if (!this.props.isMobile && !this.hasDates()) {
            this.handleDateRangeOpen();
        }
    };

    handleDateRangeChangeSubmit = () => {
        const {arrival = '', departure = ''} = this.state;
        const {
            isMobile,
            setDateRangePickerDates,
            transDates: {
                arrival: arrivalFromState,
                departure: departureFromState
            }
        } = this.props;

        if ((!!arrival && !!departure) || isMobile) {
            setDateRangePickerDates(arrival, departure);
        }

        this.handleDateRangeClose();

        if (!isMobile && !this.hasGuest()) {
            this.handleGuestPickerOpen();
        }

        if (
            ((!!arrival && !!departure) || (!arrival && !departure)) &&
            (arrival !== arrivalFromState || departure !== departureFromState)
        ) {
            this.handleOnChange();
        }
    };

    handleRevertDateRangeChange = () => {
        const state = this.state;
        const {
            isMobile,
            transDates: {arrival, departure}
        } = this.props;
        if (isMobile && !state.arrival && !state.departure) {
            this.handleDateRangeChangeSubmit();
        } else {
            this.setState({arrival, departure});
        }
        this.handleDateRangeClose();
    };

    handleDateRangeOpen = () => {
        this.props.onOpenDateRangePicker();
    };

    handleDateRangeClose = () => {
        this.props.onCloseDateRangePicker();
    };

    handleDateRangeCleared = () => {
        if (this.props.isMobile) {
            return;
        }
        this.props.setDateRangePickerDates('', '');
        this.handleOnChange({arrival: '', departure: ''});
    };

    handleGuestPickerOpen = () => {
        this.props.onOpenGuestPicker();
    };

    // exit without saving
    handleGuestSelectorExited = (undoState: Object) => {
        // We want to reset state here but not close the picker if the back button was pressed.
        // If we navigated back in history, guestPickerIsOpen would be null
        if (this.props.guestPickerIsOpen) {
            this.props.onCloseGuestPicker();
        }
        this.setState(undoState);
    };

    hasGuest = () => {
        const {
            adultsCount = 0,
            childrenCount = 0,
            petIncluded = false
        } = this.state;
        return adultsCount > 0 || childrenCount > 0 || petIncluded;
    };

    render() {
        const {locale} = this.props.intl;
        const {
            dateRangePickerIsOpen,
            guestPickerIsOpen,
            isMobile,
            searchIcon,
            showGuests,
            searchButton,
            datepickerConfig,
            typeaheadConfig,
            guestpickerConfig
        } = this.props;
        const {
            adultsCount,
            childrenCount,
            petIncluded,
            arrival,
            departure,
            q
        } = this.state;

        const defaultSearchText = q || '';
        const isDatedSearch = !!(arrival && departure);

        return (
            <div className="SearchFields">
                <div className="SearchFields__field SearchFields__field--typeahead">
                    <Typeahead
                        className="Typeahead"
                        defaultSearch={defaultSearchText}
                        search={defaultSearchText}
                        onSelectDestination={this.handleSelectDestination}
                        onClose={this.handleOnLocationChange}
                        locale={locale}
                        icon={searchIcon}
                        usePopoverAnimation={false}
                        useResultsAnimation={false}
                        {...typeaheadConfig}
                    />
                </div>
                <div
                    className={classNames('SearchFields__field', {
                        'DateRange_hover-override': arrival && !departure,
                        'text-danger__border': !isMobile && !isDatedSearch
                    })}
                >
                    <ConnectedDateRangePicker
                        dateTextClass={classNames({
                            'text-danger': !isMobile && !isDatedSearch
                        })}
                        startDate={this.state.arrival}
                        endDate={this.state.departure}
                        onDateRangeChange={this.handleDateRangeChange}
                        onFocus={this.handleDateRangeOpen}
                        onOpen={this.handleDateRangeOpen}
                        onDateRangeClear={this.handleDateRangeCleared}
                        onCancel={this.handleRevertDateRangeChange}
                        onSubmit={this.handleDateRangeChangeSubmit}
                        isOpen={dateRangePickerIsOpen}
                        {...datepickerConfig}
                    />
                    {searchButton}
                </div>
                {showGuests && (
                    <div className="SearchFields__field SearchFields__field--guest-picker">
                        <ConnectedGuestPicker
                            adultsCount={adultsCount}
                            childrenCount={childrenCount}
                            petIncluded={petIncluded}
                            onChange={this.handleGuestSelectorChange}
                            onClose={this.handleGuestSelectorClose}
                            onExit={this.handleGuestSelectorExited}
                            onOpen={this.handleGuestPickerOpen}
                            isOpen={guestPickerIsOpen}
                            {...guestpickerConfig}
                        />
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    dateRangePicker,
    modals,
    searchParams: {params},
    searchTerm: {description: searchTerm},
    viewport: {breakpoint}
}: ReduxState): StateProps => {
    const isMobile = !media(breakpoint, {minWidth: 'SM_MIN'});
    return {
        dateRangePickerIsOpen: modals.DATES.isOpen,
        guestPickerIsOpen: modals.GUESTS.isOpen,
        transDates: dateRangePicker,
        isMobile,
        isTouchDevice:
            !media(breakpoint, {minWidth: 'SM_MIN'}) ||
            !media(breakpoint, {minWidth: 'MD_MIN'}),
        ...pick(params, searchFieldParameters),
        // if searchTerm isn't provided, use the q provided in the URL
        q: searchTerm || params.q || '',
        isBoundingBoxSearch: isBoundingBoxSearch(params),
        isRegionSearch: !!params.regionId
    };
};

// NOTE: if these are not thunks, we need to dispatch
const mapDispatchToProps = {
    onOpenDateRangePicker: () => routerActions.pushModal(DATE_MODAL),
    onCloseDateRangePicker: () => routerActions.popModal(DATE_MODAL),
    onOpenGuestPicker: () => routerActions.pushModal(GUEST_MODAL),
    onCloseGuestPicker: () => routerActions.popModal(GUEST_MODAL),
    setDateRangePickerDates: dateRangePickerActions.setDateRangePickerDates
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(SearchFields);
